import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { SmartCMRLogo } from '../components/brand/SmartCMRLogo';
import { Truck, Loader2, FileText } from 'lucide-react';
import LanguageSelector from '../components/common/LanguageSelector';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CMR_LANGUAGES, localeToCmrLang } from '../data/cmrLabels';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    name: '',
    cmr_language: localeToCmrLang(language),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth.passwordsNoMatch'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }

    if (!formData.company_name.trim()) {
      toast.error(t('auth.companyNameRequired'));
      return;
    }

    setLoading(true);

    try {
      // Get acquisition/attribution data from analytics tracker (null if no consent)
      let acquisitionData = null;
      try {
        const { getAcquisitionData } = require('../utils/analyticsTracker');
        acquisitionData = getAcquisitionData();
      } catch (e) {
        // Silently fail — analytics must never block registration
      }

      const registrationData = {
        email: formData.email.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
        password: formData.password,
        name: formData.name || formData.email.split('@')[0],
        company_name: formData.company_name,
        role: 'admin',
        language: language,
        cmr_language: formData.cmr_language,
        acquisition: acquisitionData,
      };

      const result = await register(registrationData);
      
      if (result.email_verification_required) {
        sessionStorage.setItem('verification_email', result.email);
        navigate('/verification-sent', { 
          state: { email: result.email },
          replace: true 
        });
      } else {
        toast.success(t('auth.registrationSuccess'));
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'email_already_exists') {
        toast.error(t('auth.emailAlreadyExists'));
      } else {
        toast.error(detail || t('auth.registrationFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <div className="absolute right-4 z-20" style={{ top: 'max(1rem, env(safe-area-inset-top, 0px))' }}>
        <LanguageSelector />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-4">
          <Link to="/" className="flex items-center justify-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
              </div>
            <SmartCMRLogo />
          </Link>
          <CardTitle className="text-xl">{t('auth.createAccount')}</CardTitle>
          <CardDescription className="text-sm">
            {t('auth.registerSubtitle')}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">{t('auth.companyName')} *</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder={t('auth.companyNamePlaceholder')}
                required
                data-testid="company-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">{t('auth.yourName')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={t('auth.namePlaceholder')}
                required
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')} *</Label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                required
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                data-testid="email-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')} *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('auth.confirmPassword')} *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            {/* CMR Document Language */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-slate-500" />
                {t('generator.cmrLanguage') || 'CMR-taal'}
              </Label>
              <Select value={formData.cmr_language} onValueChange={(v) => handleChange('cmr_language', v)} data-testid="register-cmr-language">
                <SelectTrigger className="h-10" data-testid="register-cmr-language-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CMR_LANGUAGES).map(([code, { name, flag }]) => (
                    <SelectItem key={code} value={code}>
                      <span className="flex items-center gap-2">{flag} {name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">{t('settings.cmrLanguageDesc') || 'Taal voor vaste labels op CMR-vrachtbrieven. Later wijzigbaar.'}</p>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700 h-11 text-base font-semibold" 
              disabled={loading}
              data-testid="register-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                t('auth.createAccount')
              )}
            </Button>

            <p className="text-center text-sm text-slate-600">
              {t('auth.hasAccount')}{' '}
              <Link to="/login" className="text-blue-600 hover:underline font-medium">
                {t('auth.loginHere')}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
